import React, { useState, useEffect } from 'react';
import { Star, Quote, MapPin } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['bg-[#003456]', 'bg-[#B87333]', 'bg-[#023F63]', 'bg-[#0E5A61]', 'bg-[#8a5a2b]'];

const initialsOf = (name) => {
  const parts = (name || '').split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[1][0]).toUpperCase();
};

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < n ? 'text-[#CE8946] fill-[#CE8946]' : 'text-slate-300'}`} />
    ))}
  </div>
);

// Shows approved reviews captured for a specific page (subject e.g. "location:Calgary").
// Renders nothing until at least one review has been approved for this place.
export const LocalReviews = ({ subject, placeName, heading, blurb, testid = 'local-reviews' }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!subject) return;
    axios.get(`${API_URL}/api/site-testimonials`, { params: { subject } })
      .then((r) => { if (Array.isArray(r.data)) setItems(r.data); })
      .catch(() => { /* silent */ });
  }, [subject]);

  if (!items.length) return null;

  return (
    <section className="py-20 section-padding bg-white" data-testid={testid}>
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-10">
          <span className="inline-flex items-center gap-2 text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">
            <MapPin className="h-4 w-4" /> {heading ? 'Verified reviews' : 'Local reviews'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">
            {heading || `What ${placeName} owners say`}
          </h2>
          <p className="text-slate-600 mt-3">{blurb || `Real feedback from freeze dryer owners we've helped in ${placeName}.`}</p>
        </div>
        <div className={`grid gap-6 ${items.length >= 3 ? 'md:grid-cols-3' : items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-2xl'}`}>
          {items.slice(0, 6).map((t, i) => (
            <figure key={t.id || i} className="bg-[#F1F5F9] border border-slate-200 rounded-2xl p-7 flex flex-col hover:shadow-lg transition-shadow" data-testid={`${testid}-item-${i}`}>
              <div className="flex items-center justify-between">
                <Quote className="h-8 w-8 text-[#CE8946]/30" />
                <Stars n={t.rating || 5} />
              </div>
              <blockquote className="text-slate-700 text-sm leading-relaxed mt-4 flex-1 whitespace-pre-line">"{t.quote}"</blockquote>
              <figcaption className="flex items-center gap-3 mt-6 pt-5 border-t border-slate-200">
                <span className={`w-11 h-11 rounded-full ${t.color || COLORS[i % COLORS.length]} text-white font-bold flex items-center justify-center flex-shrink-0`}>{t.initials || initialsOf(t.name)}</span>
                <div className="leading-tight">
                  <p className="text-[#003456] font-bold text-sm">{t.name}</p>
                  {[t.role, t.location].filter(Boolean).length > 0 && (
                    <p className="text-slate-500 text-xs">{[t.role, t.location].filter(Boolean).join(' · ')}</p>
                  )}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LocalReviews;
